import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";
import { 
  DollarSign, 
  Loader2, 
  FileText, 
  Download,
  Factory,
  Package,
  Users,
  Calculator,
  TrendingUp,
  AlertCircle,
  RotateCcw,
  Printer,
  Archive,
  Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

export default function CostControl() {
  const [loading, setLoading] = useState(true);
  const [costReport, setCostReport] = useState(null);
  const [costSettings, setCostSettings] = useState(null);
  const [dateFilter, setDateFilter] = useState({
    start_date: "",
    end_date: "",
  });
  const [activeTab, setActiveTab] = useState("report");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveDetailOpen, setArchiveDetailOpen] = useState(false);

  const fetchCostReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start_date) params.start_date = dateFilter.start_date;
      if (dateFilter.end_date) params.end_date = dateFilter.end_date;
      
      const response = await axios.get(`${API}/costs/report`, { params });
      setCostReport(response.data);
    } catch (error) {
      toast.error("Erro ao carregar relatório de custos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCostSettings = async () => {
    try {
      const response = await axios.get(`${API}/costs/settings`);
      setCostSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  useEffect(() => {
    fetchCostReport();
    fetchCostSettings();
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      const response = await axios.get(`${API}/costs/archives`);
      setArchives(response.data);
    } catch (error) {
      console.error("Error fetching archives:", error);
    }
  };

  const viewArchiveDetail = async (archiveId) => {
    try {
      const response = await axios.get(`${API}/costs/archives/${archiveId}`);
      setSelectedArchive(response.data);
      setArchiveDetailOpen(true);
    } catch (error) {
      toast.error("Erro ao carregar arquivo");
    }
  };

  const updateMachineCost = async (machineId, newCost) => {
    try {
      await axios.put(`${API}/costs/machine/${machineId}?hour_cost=${newCost}`);
      toast.success("Custo atualizado");
      fetchCostSettings();
    } catch (error) {
      toast.error("Erro ao atualizar custo");
    }
  };

  const updateMaterialCost = async (materialId, newCost) => {
    try {
      await axios.put(`${API}/costs/material/${materialId}?cost_per_kg=${newCost}`);
      toast.success("Custo atualizado");
      fetchCostSettings();
    } catch (error) {
      toast.error("Erro ao atualizar custo");
    }
  };

  const updateOperatorCost = async (operatorId, newCost) => {
    try {
      await axios.put(`${API}/costs/operator/${operatorId}?hour_cost=${newCost}`);
      toast.success("Custo atualizado");
      fetchCostSettings();
    } catch (error) {
      toast.error("Erro ao atualizar custo");
    }
  };

  const resetAllCosts = async () => {
    try {
      await axios.post(`${API}/costs/reset`);
      toast.success("Custos zerados com sucesso!");
      setResetDialogOpen(false);
      fetchCostReport();
    } catch (error) {
      showError(error, "Erro ao zerar custos");
    }
  };

  const generateOrderPDF = (order) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Custos - Ordem ${order.order_code}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1a1a1a; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { height: 60px; background: white; padding: 5px; border-radius: 8px; }
          .header-info { text-align: right; }
          .header-info h1 { font-size: 22px; color: #2563eb; }
          .header-info p { color: #64748b; font-size: 12px; margin-top: 5px; }
          .order-info { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 25px; margin-bottom: 25px; }
          .order-title { font-size: 28px; font-weight: 700; color: #1e293b; margin-bottom: 15px; }
          .order-details { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
          .detail-item { padding: 10px; background: white; border-radius: 8px; }
          .detail-label { font-size: 11px; color: #64748b; text-transform: uppercase; font-weight: 600; }
          .detail-value { font-size: 16px; font-weight: 600; color: #1e293b; margin-top: 3px; }
          .costs-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 25px; }
          .cost-card { background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); border-radius: 12px; padding: 20px; text-align: center; }
          .cost-label { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; }
          .cost-value { font-size: 28px; font-weight: 700; color: #1e293b; margin-top: 5px; }
          .cost-total { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; }
          .cost-total .cost-label, .cost-total .cost-value { color: white; }
          .summary-table { width: 100%; border-collapse: collapse; margin-top: 25px; }
          .summary-table th, .summary-table td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          .summary-table th { background: #f1f5f9; font-weight: 600; }
          .text-right { text-align: right; }
          .text-green { color: #16a34a; }
          .text-red { color: #dc2626; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${LOGO_URL}" alt="Logo" class="logo" />
          <div class="header-info">
            <h1>Relatório de Custos</h1>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
          </div>
        </div>

        <div class="order-info">
          <div class="order-title">Ordem: ${order.order_code}</div>
          <div class="order-details">
            <div class="detail-item">
              <div class="detail-label">Peça</div>
              <div class="detail-value">${order.part_code}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Máquina</div>
              <div class="detail-value">${order.machine_code}</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Quantidade Boa</div>
              <div class="detail-value" style="color: #16a34a;">${order.good_quantity.toLocaleString()} un</div>
            </div>
            <div class="detail-item">
              <div class="detail-label">Quantidade Rejeitada</div>
              <div class="detail-value" style="color: #dc2626;">${order.rejected_quantity.toLocaleString()} un</div>
            </div>
          </div>
        </div>

        <div class="costs-grid">
          <div class="cost-card">
            <div class="cost-label">Matéria-Prima</div>
            <div class="cost-value">${order.material_cost.toFixed(2)}€</div>
          </div>
          <div class="cost-card">
            <div class="cost-label">Máquina</div>
            <div class="cost-value">${order.machine_cost.toFixed(2)}€</div>
          </div>
          <div class="cost-card">
            <div class="cost-label">Operador</div>
            <div class="cost-value">${order.operator_cost.toFixed(2)}€</div>
          </div>
          <div class="cost-card cost-total">
            <div class="cost-label">Custo Total</div>
            <div class="cost-value">${order.total_cost.toFixed(2)}€</div>
          </div>
        </div>

        <table class="summary-table">
          <tr>
            <th>Indicador</th>
            <th class="text-right">Valor</th>
          </tr>
          <tr>
            <td>Custo por Peça</td>
            <td class="text-right"><strong>${order.cost_per_piece.toFixed(4)}€</strong></td>
          </tr>
          <tr>
            <td>Taxa de Qualidade</td>
            <td class="text-right">${((order.good_quantity / (order.good_quantity + order.rejected_quantity)) * 100).toFixed(1)}%</td>
          </tr>
          <tr>
            <td>Custo de Rejeição Estimado</td>
            <td class="text-right text-red">${(order.cost_per_piece * order.rejected_quantity).toFixed(2)}€</td>
          </tr>
        </table>

        <div class="footer">
          EP3D ENGINEERING PROJECT - Relatório de Custos de Ordem de Produção
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const generatePDF = () => {
    if (!costReport) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Custos</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1a1a1a; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 25px; }
          .logo { height: 50px; }
          .header-info { text-align: right; }
          .header-info h1 { font-size: 20px; color: #2563eb; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 25px; }
          .stat-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; text-align: center; }
          .stat-label { font-size: 10px; color: #64748b; text-transform: uppercase; }
          .stat-value { font-size: 22px; font-weight: 700; color: #1e293b; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 20px; }
          th { background: #f1f5f9; padding: 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #e2e8f0; }
          td { padding: 8px; border-bottom: 1px solid #f1f5f9; }
          .text-right { text-align: right; }
          .text-green { color: #16a34a; }
          .text-red { color: #dc2626; }
          .footer { margin-top: 30px; font-size: 10px; color: #94a3b8; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${LOGO_URL}" alt="Logo" class="logo" />
          <div class="header-info">
            <h1>Relatório de Custos de Produção</h1>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
          </div>
        </div>

        <div class="summary-grid">
          <div class="stat-card">
            <div class="stat-label">Custo Matéria-Prima</div>
            <div class="stat-value">${costReport.totals.material_cost.toFixed(2)}€</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Custo Máquinas</div>
            <div class="stat-value">${costReport.totals.machine_cost.toFixed(2)}€</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Custo Operadores</div>
            <div class="stat-value">${costReport.totals.operator_cost.toFixed(2)}€</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Custo Total</div>
            <div class="stat-value" style="color: #2563eb;">${costReport.totals.total_cost.toFixed(2)}€</div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="stat-card">
            <div class="stat-label">Peças Boas</div>
            <div class="stat-value text-green">${costReport.totals.good_quantity.toLocaleString()}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Peças Rejeitadas</div>
            <div class="stat-value text-red">${costReport.totals.rejected_quantity.toLocaleString()}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Custo Rejeição</div>
            <div class="stat-value text-red">${costReport.totals.rejection_cost.toFixed(2)}€</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Ordens</div>
            <div class="stat-value">${costReport.count}</div>
          </div>
        </div>

        <h3 style="margin: 20px 0 10px; font-size: 14px;">Detalhe por Ordem de Produção</h3>
        <table>
          <thead>
            <tr>
              <th>Ordem</th>
              <th>Peça</th>
              <th>Máquina</th>
              <th class="text-right">Qtd. Boa</th>
              <th class="text-right">Qtd. Rej.</th>
              <th class="text-right">Mat. Prima</th>
              <th class="text-right">Máquina</th>
              <th class="text-right">Operador</th>
              <th class="text-right">Total</th>
              <th class="text-right">€/Peça</th>
            </tr>
          </thead>
          <tbody>
            ${costReport.orders.map(order => `
              <tr>
                <td><strong>${order.order_code}</strong></td>
                <td>${order.part_code}</td>
                <td>${order.machine_code}</td>
                <td class="text-right text-green">${order.good_quantity}</td>
                <td class="text-right text-red">${order.rejected_quantity}</td>
                <td class="text-right">${order.material_cost.toFixed(2)}€</td>
                <td class="text-right">${order.machine_cost.toFixed(2)}€</td>
                <td class="text-right">${order.operator_cost.toFixed(2)}€</td>
                <td class="text-right"><strong>${order.total_cost.toFixed(2)}€</strong></td>
                <td class="text-right">${order.cost_per_piece.toFixed(4)}€</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="footer">
          EP3D ENGINEERING PROJECT - Relatório de Controlo de Custos
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cost-control-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Controlo de Custos
          </h1>
          <p className="text-muted-foreground mt-1">
            Análise de custos por ordem de produção
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        <Button
          variant={activeTab === "report" ? "default" : "ghost"}
          onClick={() => setActiveTab("report")}
        >
          <FileText className="h-4 w-4 mr-2" />
          Relatório de Custos
        </Button>
        <Button
          variant={activeTab === "settings" ? "default" : "ghost"}
          onClick={() => setActiveTab("settings")}
        >
          <Calculator className="h-4 w-4 mr-2" />
          Configuração de Custos
        </Button>
        <Button
          variant={activeTab === "history" ? "default" : "ghost"}
          onClick={() => setActiveTab("history")}
        >
          <Archive className="h-4 w-4 mr-2" />
          Histórico ({archives.length})
        </Button>
      </div>

      {activeTab === "report" && (
        <>
          {/* Filters */}
          <Card className="border-border">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-2">
                  <Label>Data Início</Label>
                  <Input
                    type="date"
                    value={dateFilter.start_date}
                    onChange={(e) => setDateFilter({ ...dateFilter, start_date: e.target.value })}
                    className="w-40"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Fim</Label>
                  <Input
                    type="date"
                    value={dateFilter.end_date}
                    onChange={(e) => setDateFilter({ ...dateFilter, end_date: e.target.value })}
                    className="w-40"
                  />
                </div>
                <Button onClick={fetchCostReport}>
                  Filtrar
                </Button>
                <Button variant="outline" onClick={generatePDF} disabled={!costReport}>
                  <Download className="h-4 w-4 mr-2" />
                  Exportar PDF
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => setResetDialogOpen(true)}
                  disabled={!costReport || costReport.totals.total_cost === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Zerar Custos
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Reset Confirmation Dialog */}
          <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600">Confirmar Reset de Custos</DialogTitle>
                <DialogDescription>
                  Esta ação irá zerar todos os custos acumulados das ordens de produção. 
                  Esta operação não pode ser revertida.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
                <p className="text-red-800 font-medium">Valores que serão zerados:</p>
                {costReport && (
                  <ul className="text-red-700 text-sm mt-2 space-y-1">
                    <li>• Custo Matéria-Prima: {costReport.totals.material_cost.toFixed(2)}€</li>
                    <li>• Custo Máquinas: {costReport.totals.machine_cost.toFixed(2)}€</li>
                    <li>• Custo Operadores: {costReport.totals.operator_cost.toFixed(2)}€</li>
                    <li>• <strong>Total: {costReport.totals.total_cost.toFixed(2)}€</strong></li>
                  </ul>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button variant="destructive" onClick={resetAllCosts}>
                  Confirmar Reset
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Summary Cards */}
          {costReport && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-border bg-blue-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Package className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-blue-600">Matéria-Prima</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {costReport.totals.material_cost.toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border bg-purple-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Factory className="h-8 w-8 text-purple-600" />
                      <div>
                        <p className="text-sm text-purple-600">Máquinas</p>
                        <p className="text-2xl font-bold text-purple-700">
                          {costReport.totals.machine_cost.toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border bg-amber-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Users className="h-8 w-8 text-amber-600" />
                      <div>
                        <p className="text-sm text-amber-600">Operadores</p>
                        <p className="text-2xl font-bold text-amber-700">
                          {costReport.totals.operator_cost.toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border bg-green-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm text-green-600">Custo Total</p>
                        <p className="text-2xl font-bold text-green-700">
                          {costReport.totals.total_cost.toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Rejection Cost Alert */}
              {costReport.totals.rejection_cost > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="h-8 w-8 text-red-600" />
                      <div>
                        <p className="text-sm text-red-600">Custo de Rejeição</p>
                        <p className="text-2xl font-bold text-red-700">
                          {costReport.totals.rejection_cost.toFixed(2)}€
                        </p>
                        <p className="text-sm text-red-600">
                          ({costReport.totals.rejected_quantity} peças rejeitadas)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Orders Table */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                    <TrendingUp className="h-5 w-5" />
                    Custos por Ordem de Produção ({costReport.count})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ordem</TableHead>
                          <TableHead>Peça</TableHead>
                          <TableHead>Máquina</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Qtd. Boa</TableHead>
                          <TableHead className="text-right">Qtd. Rej.</TableHead>
                          <TableHead className="text-right">Mat. Prima</TableHead>
                          <TableHead className="text-right">Máquina</TableHead>
                          <TableHead className="text-right">Operador</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">€/Peça</TableHead>
                          <TableHead className="text-center">PDF</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costReport.orders.map((order) => (
                          <TableRow key={order.order_code}>
                            <TableCell className="font-mono font-bold text-foreground">
                              {order.order_code}
                            </TableCell>
                            <TableCell className="text-foreground">{order.part_code}</TableCell>
                            <TableCell className="text-foreground">{order.machine_code}</TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-1 rounded ${
                                order.status === "completed" ? "bg-green-100 text-green-700" :
                                order.status === "running" ? "bg-blue-100 text-blue-700" :
                                "bg-gray-100 text-gray-700"
                              }`}>
                                {order.status === "completed" ? "Concluída" :
                                 order.status === "running" ? "Em Execução" :
                                 order.status === "pending" ? "Pendente" : order.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-green-600">
                              {order.good_quantity.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-600">
                              {order.rejected_quantity.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-mono text-foreground">
                              {order.material_cost.toFixed(2)}€
                            </TableCell>
                            <TableCell className="text-right font-mono text-foreground">
                              {order.machine_cost.toFixed(2)}€
                            </TableCell>
                            <TableCell className="text-right font-mono text-foreground">
                              {order.operator_cost.toFixed(2)}€
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-foreground">
                              {order.total_cost.toFixed(2)}€
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {order.cost_per_piece.toFixed(4)}€
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => generateOrderPDF(order)}
                                title="Exportar PDF desta ordem"
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {activeTab === "settings" && costSettings && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Machine Costs */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                <Factory className="h-5 w-5" />
                Custo/Hora Máquinas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {costSettings.machines.map((machine) => (
                  <div key={machine.id} className="flex items-center justify-between gap-2 p-2 bg-secondary/30 rounded">
                    <span className="font-mono text-sm text-foreground">{machine.code}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={machine.hour_cost}
                        className="w-24 h-8 text-right"
                        onBlur={(e) => updateMachineCost(machine.id, parseFloat(e.target.value))}
                      />
                      <span className="text-sm text-muted-foreground">€/h</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Material Costs */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                <Package className="h-5 w-5" />
                Custo/kg Matéria-Prima
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {costSettings.materials.map((material) => (
                  <div key={material.id} className="flex items-center justify-between gap-2 p-2 bg-secondary/30 rounded">
                    <div>
                      <span className="font-mono text-sm text-foreground">{material.code}</span>
                      <span className="text-xs text-muted-foreground ml-2">({material.plastic_type})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={material.cost_per_kg}
                        className="w-24 h-8 text-right"
                        onBlur={(e) => updateMaterialCost(material.id, parseFloat(e.target.value))}
                      />
                      <span className="text-sm text-muted-foreground">€/kg</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Operator Costs */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                <Users className="h-5 w-5" />
                Custo/Hora Operadores
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  {costSettings.operators.length} utilizadores
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {costSettings.operators.map((operator) => {
                  const initials = (operator.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
                  const roleColor = {
                    admin: "bg-red-100 text-red-700 border-red-200",
                    operator: "bg-blue-100 text-blue-700 border-blue-200",
                    quality: "bg-emerald-100 text-emerald-700 border-emerald-200",
                    shift_leader: "bg-amber-100 text-amber-700 border-amber-200",
                  }[operator.role] || "bg-slate-100 text-slate-600 border-slate-200";
                  return (
                    <div key={operator.id} className="flex items-center justify-between gap-2 p-2 bg-secondary/30 rounded hover:bg-secondary/50 transition-colors" data-testid={`op-cost-${operator.user_code}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {operator.photo_url ? (
                          <img src={operator.photo_url} alt={operator.name} className="h-8 w-8 rounded-full object-cover shrink-0 border border-slate-200" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-[10px] shrink-0">
                            {initials}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{operator.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] font-mono text-muted-foreground">{operator.user_code}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${roleColor}`}>
                              {operator.role_label}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={operator.hour_cost}
                          className="w-24 h-8 text-right"
                          onBlur={(e) => updateOperatorCost(operator.id, parseFloat(e.target.value))}
                        />
                        <span className="text-sm text-muted-foreground">€/h</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
              <Archive className="h-5 w-5" />
              Histórico de Custos Arquivados
            </CardTitle>
          </CardHeader>
          <CardContent>
            {archives.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Nenhum arquivo de custos encontrado. Os arquivos são criados quando faz "Zerar Custos".
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data do Arquivo</TableHead>
                    <TableHead>Arquivado Por</TableHead>
                    <TableHead className="text-right">Nº Ordens</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archives.map((archive) => (
                    <TableRow key={archive.id}>
                      <TableCell>
                        {new Date(archive.archived_at).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell>{archive.archived_by}</TableCell>
                      <TableCell className="text-right font-mono">
                        {archive.orders_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewArchiveDetail(archive.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Archive Detail Dialog */}
      <Dialog open={archiveDetailOpen} onOpenChange={setArchiveDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo">
              Arquivo de Custos - {selectedArchive?.archived_at && new Date(selectedArchive.archived_at).toLocaleString('pt-PT')}
            </DialogTitle>
            <DialogDescription>
              Arquivado por: {selectedArchive?.archived_by} | {selectedArchive?.orders_count} ordens
            </DialogDescription>
          </DialogHeader>
          
          {selectedArchive && (
            <div className="space-y-4 mt-4">
              {/* Totals */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-blue-600">Matéria-Prima</p>
                    <p className="text-lg font-bold text-blue-800">{selectedArchive.totals?.material_cost?.toFixed(2)}€</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-amber-600">Máquinas</p>
                    <p className="text-lg font-bold text-amber-800">{selectedArchive.totals?.machine_cost?.toFixed(2)}€</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-green-600">Operadores</p>
                    <p className="text-lg font-bold text-green-800">{selectedArchive.totals?.operator_cost?.toFixed(2)}€</p>
                  </CardContent>
                </Card>
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-xs text-indigo-600">Total</p>
                    <p className="text-lg font-bold text-indigo-800">{selectedArchive.totals?.total_cost?.toFixed(2)}€</p>
                  </CardContent>
                </Card>
              </div>

              {/* Orders Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ordem</TableHead>
                    <TableHead className="text-right">Qtd. Boa</TableHead>
                    <TableHead className="text-right">Qtd. Rejeitada</TableHead>
                    <TableHead className="text-right">Custo MP</TableHead>
                    <TableHead className="text-right">Custo Máq.</TableHead>
                    <TableHead className="text-right">Custo Op.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedArchive.report?.map((order, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono">{order.order_code}</TableCell>
                      <TableCell className="text-right text-green-600">{order.good_quantity?.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-red-600">{order.rejected_quantity?.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{order.material_cost?.toFixed(2)}€</TableCell>
                      <TableCell className="text-right">{order.machine_cost?.toFixed(2)}€</TableCell>
                      <TableCell className="text-right">{order.operator_cost?.toFixed(2)}€</TableCell>
                      <TableCell className="text-right font-bold">{order.total_cost?.toFixed(2)}€</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
